import { Link } from "@inertiajs/react"
import type { Pagination as PaginationType } from "../types"

interface PaginationProps {
  pagination: PaginationType
  buildUrl: (page: number) => string
}

export function Pagination({ pagination, buildUrl }: PaginationProps) {
  const { current_page, total_pages } = pagination

  if (total_pages <= 1) return null

  const pages = getPageNumbers(current_page, total_pages)

  return (
    <nav className="flex items-center space-x-1">
      {current_page > 1 && (
        <Link
          href={buildUrl(current_page - 1)}
          className="px-3 py-2 text-sm font-medium text-foreground-secondary bg-card border border-border-strong rounded-md hover:bg-surface-subtle"
        >
          Previous
        </Link>
      )}

      {pages.map((page, i) =>
        page === "..." ? (
          <span
            key={`gap-${i}`}
            className="px-3 py-2 text-sm font-medium text-foreground-muted"
          >
            ...
          </span>
        ) : (
          <Link
            key={page}
            href={buildUrl(page as number)}
            className={
              page === current_page
                ? "px-3 py-2 text-sm font-medium text-brand-foreground bg-brand-strong border border-brand-strong rounded-md"
                : "px-3 py-2 text-sm font-medium text-foreground-secondary bg-card border border-border-strong rounded-md hover:bg-surface-subtle"
            }
          >
            {page}
          </Link>
        )
      )}

      {current_page < total_pages && (
        <Link
          href={buildUrl(current_page + 1)}
          className="px-3 py-2 text-sm font-medium text-foreground-secondary bg-card border border-border-strong rounded-md hover:bg-surface-subtle"
        >
          Next
        </Link>
      )}
    </nav>
  )
}

function getPageNumbers(current: number, total: number): (number | "...")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  const pages: (number | "...")[] = [1]

  if (current > 3) {
    pages.push("...")
  }

  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  if (current < total - 2) {
    pages.push("...")
  }

  pages.push(total)

  return pages
}
